import React, { useState, lazy } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { BlogPost, categories, FALLBACK_IMAGE } from '../data/blog';
interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
}
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
export function BlogCard({
  post,
  featured = false
}: BlogCardProps) {
  const category = categories.find(c => c.id === post.category)!;
  const Icon = category.icon;
  const [imgSrc, setImgSrc] = useState(post.image);
  const handleError = () => {
    if (imgSrc !== FALLBACK_IMAGE) setImgSrc(FALLBACK_IMAGE);
  };
  const CategoryLabel = ({
    tone = 'sage'
  }: {
    tone?: 'sage' | 'white';
  }) => <span className={`inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase font-medium ${tone === 'white' ? 'text-white/90' : 'text-sage-700'}`} data-id="element-30">
      <Icon size={14} strokeWidth={1.5} data-id="element-31" />
      {category.name}
    </span>;
  if (featured) {
    return <a href={post.externalUrl} target="_blank" rel="noopener noreferrer" className="group grid grid-cols-1 lg:grid-cols-2 bg-white rounded-3xl overflow-hidden shadow-soft hover:shadow-lg transition-shadow" data-id="element-32">
        <div className="aspect-[4/3] overflow-hidden relative bg-cream-100" data-id="element-33">
          <img src={imgSrc} onError={handleError} alt={post.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-34" />
          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-charcoal flex items-center gap-1.5 shadow-sm" data-id="element-35">
            <Icon size={14} strokeWidth={1.5} data-id="element-36" />
            {category.name}
          </div>
        </div>
        <div className="p-10 lg:p-14 flex flex-col justify-center" data-id="element-37">
          <div className="flex items-center gap-4 mb-6" data-id="element-38">
            <CategoryLabel data-id="element-39" />
            <span className="w-1 h-1 rounded-full bg-charcoal-50/30" data-id="element-40" />
            <span className="text-xs text-charcoal-50 tracking-wide" data-id="element-41">
              {formatDate(post.date)}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-5 leading-tight group-hover:text-sage-700 transition-colors" data-id="element-42">
            {post.title}
          </h2>
          <p className="text-charcoal-50 font-light leading-relaxed mb-8" data-id="element-43">
            {post.excerpt}
          </p>
          <span className="inline-flex items-center text-sage-700 text-sm tracking-wide" data-id="element-44">
            Read the full reflection
            <ArrowUpRight size={16} strokeWidth={1.5} className="ml-2 transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" data-id="element-45" />
          </span>
        </div>
      </a>;
  }
  return <a href={post.externalUrl} target="_blank" rel="noopener noreferrer" className="group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-md hover:-translate-y-1 transition-all flex flex-col" data-id="element-46">
      <div className="aspect-[16/10] overflow-hidden bg-cream-200" data-id="element-47">
        <img src={imgSrc} onError={handleError} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-48" />
      </div>
      <div className="p-7 flex flex-col flex-grow" data-id="element-49">
        <div className="mb-4" data-id="element-50">
          <CategoryLabel data-id="element-51" />
        </div>
        <h3 className="text-xl font-serif text-charcoal mb-3 leading-snug group-hover:text-sage-700 transition-colors" data-id="element-52">
          {post.title}
        </h3>
        <p className="text-charcoal-50 font-light text-sm leading-relaxed mb-6 flex-grow" data-id="element-53">
          {post.excerpt}
        </p>
        <div className="flex items-center justify-between pt-4 border-t border-cream-200 mt-auto" data-id="element-54">
          <span className="text-xs text-charcoal-50 tracking-wide" data-id="element-55">
            {formatDate(post.date)}
          </span>
          <span className="inline-flex items-center text-sage-700 text-sm" data-id="element-56">
            Read
            <ArrowUpRight size={14} strokeWidth={1.5} className="ml-1 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" data-id="element-57" />
          </span>
        </div>
      </div>
    </a>;
}